import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './TransactionHistory.css';

const TransactionHistory = ({ transactions, setTransactions, account }) => {
  const { t } = useTranslation(['common', 'history']);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Normalize account address
  const normalizedAccount = account ? account.toLowerCase() : null;
  
  // Load transactions when account changes
  useEffect(() => {
    if (!normalizedAccount) {
      setWalletTransactions([]);
      return;
    }
    
    console.log(`Loading transactions for normalized account: ${normalizedAccount}`);
    
    // Check all localStorage keys to find any matches (case-insensitive)
    const allKeys = Object.keys(localStorage);
    const transactionKeys = allKeys.filter(key => 
      key.toLowerCase().includes(normalizedAccount) || 
      key.startsWith('dex_transactions_')
    );
    
    // Find exact match or create new key
    const exactKey = `dex_transactions_${normalizedAccount}`;
    
    // Also check for any keys that might match this account (different case)
    const matchingKeys = transactionKeys.filter(key => 
      key.toLowerCase().includes(normalizedAccount.toLowerCase())
    );
    
    // Try to load from exact key first
    let foundTransactions = false;
    try {
      const savedData = localStorage.getItem(exactKey);
      
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          console.log(`Found ${parsedData.length} transactions for ${normalizedAccount} (exact match)`);
          setWalletTransactions(parsedData);
          foundTransactions = true;
        }
      }
      
      // If no exact match found, try matching keys
      if (!foundTransactions && matchingKeys.length > 0) {
        for (const key of matchingKeys) {
          try {
            const altData = localStorage.getItem(key);
            if (altData) {
              const altParsed = JSON.parse(altData);
              if (Array.isArray(altParsed) && altParsed.length > 0) {
                console.log(`Found ${altParsed.length} transactions for ${normalizedAccount} using key: ${key}`);
                setWalletTransactions(altParsed);
                
                // Optionally migrate data to normalized key
                localStorage.setItem(exactKey, altData);
                console.log(`Migrated data to normalized key: ${exactKey}`);
                
                foundTransactions = true;
                break;
              }
            }
          } catch (innerError) {
            console.error(`Error checking alternative key ${key}:`, innerError);
          }
        }
      }
      
      // If still no transactions found anywhere
      if (!foundTransactions) {
        console.log(`No transactions found for ${normalizedAccount} in any storage location`);
        setWalletTransactions([]);
      }
    } catch (error) {
      console.error(`Error loading transactions for ${normalizedAccount}:`, error);
      setWalletTransactions([]);
    }
  }, [normalizedAccount]);

  // Show confirmation modal
  const openClearHistoryModal = () => {
    setShowConfirmModal(true);
  };
  
  // Clear history after confirmation
  const confirmClearHistory = () => {
    if (!normalizedAccount) return;
    
    try {
      const storageKey = `dex_transactions_${normalizedAccount}`;
      localStorage.removeItem(storageKey);
      
      // Update local state
      setWalletTransactions([]);
      
      // Also update parent component state if provided
      if (setTransactions) {
        setTransactions([]);
      }
      
      console.log(`Transaction history cleared for wallet: ${normalizedAccount}`);
      // Close the modal
      setShowConfirmModal(false);
    } catch (error) {
      console.error("Error clearing transaction history:", error);
    }
  };

  // Cancel clearing
  const cancelClearHistory = () => {
    setShowConfirmModal(false);
  };

  // Group transactions by date for a better UI display
  const groupTransactionsByDate = (transactions) => {
    const grouped = {};
    
    transactions.forEach(tx => {
      // Create date string without time
      const date = new Date(tx.timestamp).toLocaleDateString();
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(tx);
    });
    
    return grouped;
  };

  // Filter transactions based on type
  const getFilteredTransactions = () => {
    if (activeFilter === 'all') {
      return walletTransactions;
    }
    return walletTransactions.filter(tx => tx.type === activeFilter);
  };

  // Helper function to render different transaction types
  const renderTransactionContent = (tx) => {
    switch (tx.type) {
      case 'SWAP':
        // Check if we have proper token information
        if (tx.fromToken && tx.toToken && tx.fromAmount && tx.toAmount) {
          return (
            <div className="token-exchange">
              <div className="token-amounts">
                <span className="amount">{parseFloat(tx.fromAmount).toFixed(2)}</span>
                <span className="token">{tx.fromToken}</span>
                <span className="arrow">→</span>
                <span className="amount">{parseFloat(tx.toAmount).toFixed(2)}</span>
                <span className="token">{tx.toToken}</span>
              </div>
            </div>
          );
        } else {
          // Fallback for old transaction format that only has hash
          return (
            <div className="token-pair">
              {t('history:transaction.unknown')}: {tx.hash ? tx.hash.substring(0, 10) + '...' : t('history:transaction.unknown')}
            </div>
          );
        }
        
      case 'ADD_LIQUIDITY':
        return (
          <div className="liquidity-action">
            <div className="tokens-added">
              <span className="amount">{parseFloat(tx.tokenAAmount).toFixed(2)}</span>
              <span className="token">{tx.tokenA}</span>
              <span className="plus">+</span>
              <span className="amount">{parseFloat(tx.tokenBAmount).toFixed(2)}</span>
              <span className="token">{tx.tokenB}</span>
            </div>
            {tx.lpTokens && 
              <div className="lp-received">
                {t('history:tokens.lpReceived', { amount: parseFloat(tx.lpTokens).toFixed(2) })}
              </div>
            }
          </div>
        );
        
      case 'REMOVE_LIQUIDITY':
        return (
          <div className="liquidity-action">
            <div className="lp-burned">
              {t('history:tokens.lpBurned', { amount: parseFloat(tx.lpTokens).toFixed(2) })}
            </div>
            <div className="tokens-received">
              {t('history:tokens.received')} 
              <span className="amount">{parseFloat(tx.tokenAAmount).toFixed(2)}</span>
              <span className="token">{tx.tokenA}</span>
              <span className="plus">+</span>
              <span className="amount">{parseFloat(tx.tokenBAmount).toFixed(2)}</span>
              <span className="token">{tx.tokenB}</span>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="unknown-transaction">
            {t('history:transaction.unknown')}: {tx.hash ? tx.hash.substring(0, 10) + '...' : t('history:transaction.unknown')}
          </div>
        );
    }
  };

  // Get the appropriate icon for a transaction type
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'SWAP':
        return '↔️';
      case 'ADD_LIQUIDITY':
        return '⬇️';
      case 'REMOVE_LIQUIDITY':
        return '⬆️';
      default:
        return '📝';
    }
  };

  // Format the transaction type for display
  const formatTransactionType = (type) => {
    switch (type) {
      case 'SWAP':
        return t('history:transaction.swap');
      case 'ADD_LIQUIDITY':
        return t('history:transaction.addLiquidity');
      case 'REMOVE_LIQUIDITY':
        return t('history:transaction.removeLiquidity');
      default:
        return t('history:transaction.unknown');
    }
  };

  // Get filtered and grouped transactions
  const filteredTransactions = getFilteredTransactions();
  const groupedTransactions = groupTransactionsByDate(filteredTransactions);

  return (
    <div className="transaction-history-container">
      <div className="history-header">
        <h2>{t('history:title')}</h2>
        
        {walletTransactions.length > 0 && normalizedAccount && (
          <button className="clear-button" onClick={openClearHistoryModal}>
            {t('history:clear')}
          </button>
        )}
      </div>
      
      {/* Current wallet info display */}
      {account && (
        <div className="current-wallet-info">
          {t('history:wallet.current', { 
            address: `${account.substring(0, 6)}...${account.substring(account.length - 4)}`
          })}
        </div>
      )}
      
      {/* Filter controls */}
      {account && walletTransactions.length > 0 && (
        <div className="transaction-filters">
          <button 
            className={`filter-button ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            {t('history:filter.all')}
          </button>
          <button 
            className={`filter-button ${activeFilter === 'SWAP' ? 'active' : ''}`}
            onClick={() => setActiveFilter('SWAP')}
          >
            <span className="filter-icon">↔️</span> {t('history:filter.swaps')}
          </button>
          <button 
            className={`filter-button ${activeFilter === 'ADD_LIQUIDITY' ? 'active' : ''}`}
            onClick={() => setActiveFilter('ADD_LIQUIDITY')}
          >
            <span className="filter-icon">⬇️</span> {t('history:filter.addLiquidity')}
          </button>
          <button 
            className={`filter-button ${activeFilter === 'REMOVE_LIQUIDITY' ? 'active' : ''}`}
            onClick={() => setActiveFilter('REMOVE_LIQUIDITY')}
          >
            <span className="filter-icon">⬆️</span> {t('history:filter.removeLiquidity')}
          </button>
        </div>
      )}
      
      {!account && (
        <div className="warning-message">
          {t('common:warnings.historyConnect')}
        </div>
      )}
      
      {account && walletTransactions.length === 0 ? (
        <div className="no-transactions">
          <div className="empty-state-icon">📜</div>
          <p>{t('history:empty.title')}</p>
          <p className="hint">{t('history:empty.description')}</p>
        </div>
      ) : account ? (
        <div className="transactions-by-date">
          {Object.entries(groupedTransactions).map(([date, txs]) => (
            <div key={date} className="date-group">
              <div className="date-header">
                <span className="calendar-icon">📅</span>
                <span className="date-label">{date}</span>
              </div>
              
              <div className="transaction-list">
                {txs.map((tx, index) => (
                  <div 
                    key={`tx-${index}-${tx.hash || ''}-${normalizedAccount}`} 
                    className={`transaction-card ${(tx.type || 'unknown').toLowerCase()}`}
                  >
                    <div className="transaction-icon">
                      {getTransactionIcon(tx.type)}
                    </div>
                    
                    <div className="transaction-content">
                      <div className="transaction-header">
                        <div className="transaction-type">
                          {formatTransactionType(tx.type)}
                        </div>
                        <div className="transaction-time">
                          {tx.timestamp ? new Date(tx.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                        </div>
                      </div>
                      
                      <div className="transaction-details">
                        {renderTransactionContent(tx)}
                      </div>
                      
                      {tx.hash && (
                        <div className="transaction-actions">
                          <a
                            href={`https://sepolia.etherscan.io/tx/${tx.hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="etherscan-link"
                          >
                            {t('history:transaction.etherscan')}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="confirm-modal-backdrop">
          <div className="confirm-modal">
            <h3>{t('history:clearModal.title')}</h3>
            <p>{t('history:clearModal.description')}</p>
            
            <div className="confirm-modal-actions">
              <button 
                className="cancel-button" 
                onClick={cancelClearHistory}
              >
                {t('history:clearModal.cancel')}
              </button>
              <button 
                className="confirm-button" 
                onClick={confirmClearHistory}
              >
                {t('history:clearModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;